/**
 * The recording strip on a call that has no audio, and the one case where that
 * is repairable.
 *
 * ── The bug this exists for happened here ──────────────────────────────────
 *
 * A recording created in the ie1 region was fetched from `api.twilio.com`, which
 * answered 404. A 404 straight after the callback is indistinguishable from "the
 * file is not ready yet", so the sweep retried once a minute for a day, wrote
 * `failed` and left the queue for good. Twilio still held every one of those
 * files. The host bug was fixed; the rows it had already abandoned stayed
 * abandoned, because nothing in the product could ask again.
 *
 * So the button is only offered where it can actually work: the row must still
 * carry a `recordingSid` (which is the proof Twilio ever had a file, and which a
 * deleted or stopped recording no longer has). Everything else keeps the plain
 * sentence it always had - "no recording" and "still downloading" are answers,
 * not faults, and a retry button beside them would invite a click that does
 * nothing.
 */
import { useState } from "react";
import { Mic, RefreshCw } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { refetchCallRecording, type CallDetail } from "../../../lib/calls";
import { IconBtn } from "../../ui";

export function RecordingRetry({ call, onFetched }: { call: CallDetail; onFetched: () => void }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // `failed` is the sweep having given up; `none` is a callback that never
  // arrived at all. Both are permanent without a hand on them, and both are
  // recoverable while Twilio still holds the sid.
  const retryable =
    !!call.recordingSid && (call.recordingStatus === "failed" || call.recordingStatus === "none");

  async function run() {
    setBusy(true);
    setFailed(false);
    try {
      const res = await refetchCallRecording(call.id);
      // Re-read either way. `fetched: false` still moved the row to `pending`,
      // and a strip that carried on saying "failed" would send the operator to
      // press the same button again while the sweep is already on it.
      onFetched();
      if (!res.fetched) setFailed(true);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-none items-center gap-2.5 border-b border-[var(--crm-hair)] px-6 py-4 text-[12.75px] text-muted-foreground">
      <Mic className="size-4" />
      <span className="min-w-0 flex-1">
        {t(`calls.recording.${call.recordingStatus === "pending" ? "pending" : "none"}`)}
        {/* Said only after a press. Offering "this might be recoverable" on every
            recordingless call would put a hopeful sentence on the majority of
            them, which were simply never recorded. */}
        {failed ? <span className="ml-1.5">{t("calls.recording.retryFailed")}</span> : null}
      </span>
      {retryable ? (
        <IconBtn small onClick={() => void run()} disabled={busy} title={t("calls.recording.retry")}>
          <RefreshCw className={cn("size-3.5", busy && "animate-spin")} />
        </IconBtn>
      ) : null}
    </div>
  );
}
